import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ShippingStatus, ShippingCompany } from '../types';

const ALL_STATUSES: ShippingStatus[] = [
  'to_prepare',
  'in_preparation',
  'ready',
  'verified',
  'shipped',
  'received',
];

const SHIPPING_COMPANIES: ShippingCompany[] = [
  'canada_post',
  'fedex',
  'ups',
  'dhl',
  'purolator',
  'planet_courier',
  'atek',
  'other',
];

interface ShippingsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedStatuses: ShippingStatus[];
  onStatusChange: (statuses: ShippingStatus[]) => void;
  shippingCompany: string;
  onCompanyChange: (value: string) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  onClearAll?: () => void;
  hasActiveFilters?: boolean;
}

export function ShippingsFilters({
  search,
  onSearchChange,
  selectedStatuses,
  onStatusChange,
  shippingCompany,
  onCompanyChange,
  pageSize,
  onPageSizeChange,
  onClearAll,
  hasActiveFilters,
}: ShippingsFiltersProps) {
  const { t } = useTranslation('shippings');

  const handleStatusToggle = (status: ShippingStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusChange([...selectedStatuses, status]);
    }
  };

  const handleClearStatuses = () => {
    onStatusChange([]);
  };

  const statusButtonLabel =
    selectedStatuses.length === 0
      ? t('filters.allStatuses', 'All Statuses')
      : selectedStatuses.length === ALL_STATUSES.length
        ? t('filters.allStatuses', 'All Statuses')
        : `${selectedStatuses.length} Status${selectedStatuses.length > 1 ? 'es' : ''}`;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder={t('filters.searchPlaceholder', 'Search by quote #...')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64 pl-8"
        />
      </div>

      {/* Status Filter (Multi-select) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-40 justify-between">
            {statusButtonLabel}
            {selectedStatuses.length > 0 && selectedStatuses.length < ALL_STATUSES.length && (
              <X
                className="h-3 w-3 ml-1 hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearStatuses();
                }}
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {ALL_STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={selectedStatuses.includes(status)}
              onCheckedChange={() => handleStatusToggle(status)}
            >
              {t(`status.${status}`, status)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Shipping Company Filter */}
      <Select value={shippingCompany} onValueChange={onCompanyChange}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder={t('filters.allCompanies', 'All Companies')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.allCompanies', 'All Companies')}</SelectItem>
          {SHIPPING_COMPANIES.map((company) => (
            <SelectItem key={company} value={company}>
              {t(`company.${company}`, company)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Page Size */}
      <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10">10</SelectItem>
          <SelectItem value="25">25</SelectItem>
          <SelectItem value="50">50</SelectItem>
          <SelectItem value="100">100</SelectItem>
        </SelectContent>
      </Select>

      {/* Clear All Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          {t('filters.clearAll', 'Clear all')}
        </Button>
      )}

    </div>
  );
}
